import { CameraControlsImpl } from '@react-three/drei';
import { invalidate, useThree } from '@react-three/fiber';
import { Components, OrthoPerspectiveCamera, SimpleScene, SimpleWorld, Worlds } from '@thatopen/components';
import { useEffect, useRef } from 'react';
import { Vector2, Vector3 } from 'three';
import { useShallow } from 'zustand/shallow';

import { CameraProjectionType } from 'app/modules/wallforming/components/scenes/camera-controls/types/cameraProjectionType';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const projectionSyncTarget = new Vector3();

const getActiveCamera = (camera: OrthoPerspectiveCamera, projectionMode: CameraProjectionType) =>
    projectionMode === CameraProjectionType.ORTHOGRAPHIC ? camera.threeOrtho : camera.threePersp;

export const useOBCSetup = () => {
    const obcComponentsRef = useRef<Components | null>(null);
    const obcWorldRef = useRef<SimpleWorld | null>(null);
    const obcCameraRef = useRef<OrthoPerspectiveCamera | null>(null);

    const {
        mainCameraControlsRef,
        mainCameraProjectionType,
        setMainOrthographicCameraRef,
        setMainPerspectiveCameraRef,
        syncMainCameras
    } = usePlacementStore(
        useShallow((state) => ({
            mainCameraControlsRef: state.mainCameraControlsRef,
            mainCameraProjectionType: state.mainCameraProjectionType,
            setMainOrthographicCameraRef: state.setMainOrthographicCameraRef,
            setMainPerspectiveCameraRef: state.setMainPerspectiveCameraRef,
            syncMainCameras: state.syncMainCameras
        }))
    );

    const gl = useThree((state) => state.gl);
    const setThree = useThree((state) => state.set);

    // OBC setup: creates threeOrtho and threePersp camera objects
    useEffect(() => {
        const components = new Components();
        const worlds = components.get(Worlds);
        const world = worlds.create();
        world.scene = new SimpleScene(components);
        const camera = new OrthoPerspectiveCamera(components);

        const renderer = {
            three: gl,
            worlds: new Map<string, SimpleWorld>(),
            currentWorld: null,
            onWorldChanged: { trigger: () => undefined },
            isResizeable: () => true,
            getSize: () => new Vector2(gl.domElement.clientWidth, gl.domElement.clientHeight),
            dispose: () => undefined,
            update: () => undefined
        };

        world.renderer = renderer as never;
        world.camera = camera;
        components.init();

        obcComponentsRef.current = components;
        obcWorldRef.current = world;
        obcCameraRef.current = camera;

        camera.threeOrtho.layers.enableAll();
        camera.threeOrtho.up.set(0, 0, 1);

        camera.threePersp.layers.enableAll();
        camera.threePersp.up.set(0, 0, 1);

        setMainOrthographicCameraRef(camera.threeOrtho);
        setMainPerspectiveCameraRef(camera.threePersp);
        syncMainCameras(gl.domElement);

        // Set orthographic camera as default
        const activeCamera = camera.threeOrtho;
        const controls = mainCameraControlsRef.current;
        if (controls) {
            controls.camera = activeCamera;
            controls.mouseButtons.wheel = CameraControlsImpl.ACTION.ZOOM;
        }

        setThree({ camera: activeCamera });
        syncMainCameras(gl.domElement);
        invalidate();

        return () => {
            const cameraInstance = obcCameraRef.current;
            const worldInstance = obcWorldRef.current;
            const componentsInstance = obcComponentsRef.current;

            obcCameraRef.current = null;
            obcWorldRef.current = null;
            obcComponentsRef.current = null;

            worldInstance?.dispose(false);
            cameraInstance?.dispose();
            componentsInstance?.dispose();
        };
    }, [
        gl,
        mainCameraControlsRef,
        setMainOrthographicCameraRef,
        setMainPerspectiveCameraRef,
        setThree,
        syncMainCameras
    ]);

    // Handle projection type switch
    useEffect(() => {
        const controls = mainCameraControlsRef.current;
        const obcCamera = obcCameraRef.current;

        if (!controls || !obcCamera) {
            return;
        }

        // Ensure that cameras are synchronized to the current view
        syncMainCameras(gl.domElement);

        const activeCamera = getActiveCamera(obcCamera, mainCameraProjectionType);
        activeCamera.layers.enableAll();
        activeCamera.up.set(0, 0, 1);
        activeCamera.updateProjectionMatrix();
        controls.getTarget(projectionSyncTarget);
        controls.camera = activeCamera;

        controls.setLookAt(
            activeCamera.position.x,
            activeCamera.position.y,
            activeCamera.position.z,
            projectionSyncTarget.x,
            projectionSyncTarget.y,
            projectionSyncTarget.z,
            false
        );

        controls.normalizeRotations();
        controls.update(0);

        // Set wheel action - ZOOM for orthographic and DOLLY for perspective
        controls.mouseButtons.wheel =
            mainCameraProjectionType === CameraProjectionType.ORTHOGRAPHIC
                ? CameraControlsImpl.ACTION.ZOOM
                : CameraControlsImpl.ACTION.DOLLY;

        setThree({ camera: activeCamera });
        invalidate();
    }, [gl.domElement, mainCameraControlsRef, mainCameraProjectionType, setThree, syncMainCameras]);
};
